import React, { useRef } from "react";
import { PopoverBody, UncontrolledPopover } from "reactstrap";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";

const infoIconSrc = "/images/ai-answer-metadata/info-icon.svg";
const confidenceHelpUrl = "https://help.exchange.3eco.com/en/articles/14691584-ai-supplier-document-agent";

const CONFIDENCE_DETAILS = Object.freeze([
    {
        label: "Source Strength:",
        description: "Quality and reliability of source documents"
    },
    {
        label: "Match Quality:",
        description: "How well documents match the query"
    },
    {
        label: "Data Freshness:",
        description: "How current the source information is"
    },
    {
        label: "Regulatory Confidence:",
        description: "Alignment with regulatory requirements"
    },
    {
        label: "Rules Consistency:",
        description: "Consistency with defined compliance rules"
    },
    {
        label: "Citation Quality:",
        description: "Traceability and strength of cited references"
    }
]);

interface AIAnswerMetadataConfidenceBadgeProps {
    localizer: IStringLocalizer;
    confidenceLabel: string;
}

const CONFIDENCE_BADGE_CLASS_BY_LEVEL: Record<string, string> = Object.freeze({
    "high confidence": "ai-answer-field__confidence-badge--high",
    "low confidence": "ai-answer-field__confidence-badge--low",
    "insufficient data": "ai-answer-field__confidence-badge--insufficient"
});

const getConfidenceBadgeClass = (confidenceLabel: string): string => (
    CONFIDENCE_BADGE_CLASS_BY_LEVEL[confidenceLabel.toLowerCase()]
    ?? CONFIDENCE_BADGE_CLASS_BY_LEVEL["insufficient data"]
);

const AIAnswerMetadataConfidenceBadge: React.FC<AIAnswerMetadataConfidenceBadgeProps> = ({
    localizer,
    confidenceLabel
}) => {
    const confidenceBadgeClassName = getConfidenceBadgeClass(confidenceLabel);
    const confidenceBadgeIdRef = useRef(`ai-answer-field-confidence-${Math.random().toString(36).slice(2, 11)}`);
    const confidenceBadgeId = confidenceBadgeIdRef.current;

    return (
        <>
            <span
                id={confidenceBadgeId}
                className={`ai-answer-field__confidence-badge ${confidenceBadgeClassName}`}
                aria-label={localizer.localizeString("Confidence details")}
                tabIndex={0}
            >
                {localizer.localizeString(confidenceLabel)}
                <img src={infoIconSrc} className="ai-answer-field__info-icon" aria-hidden="true" alt="" />
            </span>

            <UncontrolledPopover
                trigger="hover focus"
                delay={{ show: 100, hide: 220 }}
                placement="top"
                target={confidenceBadgeId}
                container="body"
                className="ai-answer-field__confidence-popover"
                offset="0,10"
                autohide={false}
                fade={false}
            >
                <PopoverBody>
                    <div className="ai-answer-field__confidence-static-content">
                        <div className="ai-answer-field__confidence-static-item">
                            <strong className="ai-answer-field__confidence-static-title">{localizer.localizeString("Confidence is based on:")}</strong>
                        </div>

                        {CONFIDENCE_DETAILS.map(detail =>
                            <div className="ai-answer-field__confidence-static-item" key={detail.label}>
                                <strong className="ai-answer-field__confidence-static-title">{localizer.localizeString(detail.label)}</strong>
                                <span className="ai-answer-field__confidence-static-description">{localizer.localizeString(detail.description)}</span>
                            </div>
                        )}

                        <div className="ai-answer-field__confidence-static-link-row">
                            <a
                                href={confidenceHelpUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ai-answer-field__confidence-static-link"
                            >
                                {localizer.localizeString("Learn More")}
                            </a>
                        </div>
                    </div>
                </PopoverBody>
            </UncontrolledPopover>
        </>
    );
};

export default AIAnswerMetadataConfidenceBadge;
